import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/v1'

async function apiFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string }
    throw new Error(body.error || 'Request failed')
  }
  return res.json() as Promise<T>
}

export interface Order {
  id: string
  userId: string | null
  vertical: string
  status: string
  toName: string
  toAddressLine1: string
  toSuburb: string
  toState: string
  toPostcode: string
  fromName: string
  costCents: number
  paymentStatus: string
  paymentMethod: string | null
  paymentUrl: string | null
  verticalData: string
  message: string | null
  createdAt: string
  updatedAt: string
}

export interface SendLetterInput {
  to: {
    name: string
    addressLine1: string
    addressLine2?: string
    suburb: string
    state: string
    postcode: string
  }
  from: {
    name: string
    addressLine1: string
    addressLine2?: string
    suburb: string
    state: string
    postcode: string
  }
  content: string
  colour?: boolean
  priority?: boolean
  registered_post?: boolean
  handwritten_address?: boolean
  webhookUrl?: string
  email?: string
}

export interface SendLetterResponse {
  order_id: string
  status: string
  cost: string
  cost_cents: number
  payment_method?: string
  balance_after?: string
  payment_url?: string
  message?: string
}

export function useOrders(filters?: { vertical?: string; status?: string }) {
  const params = new URLSearchParams()
  if (filters?.vertical) params.set('vertical', filters.vertical)
  if (filters?.status) params.set('status', filters.status)
  const qs = params.toString() ? `?${params}` : ''

  return useQuery<{ orders: Order[]; count: number }>({
    queryKey: ['orders', filters],
    queryFn: () => apiFetch(`/orders${qs}`),
  })
}

export function useOrder(id: string) {
  return useQuery<{ order: Order }>({
    queryKey: ['order', id],
    queryFn: () => apiFetch(`/orders/${id}`),
    enabled: !!id,
  })
}

export function useSendLetter() {
  const qc = useQueryClient()
  return useMutation<SendLetterResponse, Error, SendLetterInput>({
    mutationFn: (data) => apiFetch('/letters', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  })
}

export function useCostEstimate() {
  return useMutation<{ vertical: string; cost: string; cost_cents: number }, Error, { vertical: string; options: Record<string, unknown> }>({
    mutationFn: (data) =>
      apiFetch('/orders/estimate', { method: 'POST', body: JSON.stringify(data) }),
  })
}
